import React, { useState, useEffect } from 'react';
import { PersonRegular, PersonAddRegular, BriefcaseRegular, SearchRegular, ChevronRightRegular, MailRegular, CheckmarkRegular, ArrowLeftRegular, PhoneRegular } from '@fluentui/react-icons';
import contactService from '../services/contactService';
import contactDetailsService from '../services/contactDetailsService';
import contactSubTypesService from '../services/contactSubTypesService';
import incidentCommunicationsService from '../services/incidentCommunicationsService';
import { useLanguage } from '../contexts/LanguageContext';
import { useTextSize } from '../contexts/TextSizeContext';

export default function ContactCreator({ onContactCreated }) {
  const { t } = useLanguage();
  const { currentTextSize } = useTextSize();
  const [step, setStep] = useState('initial'); // initial, selectAction, search, selectType, createPerson, createSupplier
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [senderEmail, setSenderEmail] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [linkingContact, setLinkingContact] = useState(false);
  const [supplierSubTypes, setSupplierSubTypes] = useState([]);
  const [loadingSubTypes, setLoadingSubTypes] = useState(false);
  
  // Form data - Initialize with sender email
  const [formData, setFormData] = useState({
    salutation: '',
    name: '',
    surname: '',
    email: '',
    phone: '',
    type: 'person',
    sub_type: null,
    preffered_contact_type: 'digital',
    notes: ''
  });

  // Fetch email from thread data on component mount
  useEffect(() => {
    const fetchEmailFromThread = async () => {
      try {
        console.log('🔍 Fetching email from thread data...');
        const threadData = await incidentCommunicationsService.getThreadData();
        console.log('📧 Thread data received:', threadData);
        
        // Handle nested data structure: threadData.data.communication_addresses
        const data = threadData?.data || threadData;
        console.log('📦 Extracted data object:', data);
        
        if (data && data.communication_addresses && data.communication_addresses.length > 0) {
          const email = data.communication_addresses[0];
          console.log('✅ Email extracted from thread:', email);
          setSenderEmail(email);
          setFormData(prev => ({ ...prev, email }));
        } else {
          console.warn('⚠️ No communication_addresses found in thread data');
          console.warn('Data structure:', data);
        }
      } catch (error) {
        console.error('❌ Error fetching email from thread:', error);
      }
    };

    fetchEmailFromThread();
  }, []);

  // Update email when senderEmail changes
  useEffect(() => {
    console.log('📧 Sender email state changed to:', senderEmail);
    if (senderEmail) {
      setFormData(prev => ({ ...prev, email: senderEmail }));
    }
  }, [senderEmail]);

  // Fetch supplier sub-types from API when creating a supplier
  useEffect(() => {
    if (step === 'createSupplier' && supplierSubTypes.length === 0 && !loadingSubTypes) {
      const fetchSupplierSubTypes = async () => {
        setLoadingSubTypes(true);
        try {
          console.log('📋 Fetching contact sub-types...');
          const result = await contactSubTypesService.getAll({}, { page: 1, limit: 100 });
          console.log('✅ Contact sub-types fetched:', result);
          
          // Extract the data array from the response
          const subTypes = result.data || [];
          
          // Extract labels from sub-types (all items are supplier types)
          const supplierTypes = subTypes.map(subType => subType.label);
          
          console.log('📦 Supplier sub-types:', supplierTypes);
          
          if (supplierTypes.length > 0) {
            setSupplierSubTypes(supplierTypes);
            // Set the first option as default
            setFormData(prev => ({ ...prev, sub_type: supplierTypes[0] }));
          } else {
            // Fallback if no supplier types found
            setSupplierSubTypes([t('contactCreator.otherSupplierType')]);
            setFormData(prev => ({ ...prev, sub_type: t('contactCreator.otherSupplierType') }));
          }
        } catch (error) {
          console.error('❌ Error fetching contact sub-types:', error);
          // Fallback to default sub-types if API fails
          setSupplierSubTypes([t('contactCreator.otherSupplierType')]);
          setFormData(prev => ({ ...prev, sub_type: t('contactCreator.otherSupplierType') }));
        } finally {
          setLoadingSubTypes(false);
        }
      };

      fetchSupplierSubTypes();
    }
  }, [step, supplierSubTypes.length, loadingSubTypes]);

  // Search contacts with debounce
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      setSelectedContact(null);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setSearching(true);
      setError(null);
      try {
        const result = await contactService.searchContacts(searchQuery);
        // Handle nested data structure: response.data.data
        const contacts = result.data?.data || result.data || [];
        console.log('Search results:', contacts);
        setSearchResults(contacts);
      } catch (err) {
        console.error('Search error:', err);
        setError(err.message);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleInputChange = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSelectContact = (contact) => {
    // Show confirmation dialog
    console.log('👤 Contact selected:', contact);
    console.log('📧 Sender email for linking:', senderEmail);
    setSelectedContact(contact);
    setError(null);
  };

  const handleConfirmLinkContact = async () => {
    console.log('🔗 Starting link process...');
    console.log('📧 Sender email:', senderEmail);
    console.log('👤 Selected contact:', selectedContact);
    
    if (!selectedContact || !senderEmail) {
      console.error('❌ Missing data - Contact:', selectedContact, 'Email:', senderEmail);
      setError(t('contactCreator.missingData'));
      return;
    }

    setLinkingContact(true);
    setError(null);

    try {
      console.log('📝 Creating contact detail...');
      // Add the email to the contact's contact_details
      const detailResult = await contactDetailsService.createContactDetail({
        contact_id: selectedContact.id,
        type: 'email',
        value: senderEmail,
        label: 'primary'
      });
      console.log('✅ Contact detail created:', detailResult);

      // Set the contact ID in the service
      console.log('🔗 Setting contact ID in service...');
      contactService.setContactId(selectedContact.id);
      
      // Fetch the complete contact data
      console.log('📥 Fetching complete contact data...');
      const contactData = await contactService.getContact();
      console.log('✅ Contact data fetched:', contactData);
      
      onContactCreated(contactData.data);
    } catch (err) {
      console.error('❌ Error linking contact:', err);
      setError(err.message || t('contactCreator.linkError'));
    } finally {
      setLinkingContact(false);
    }
  };

  const handleCancelLinkContact = () => {
    setSelectedContact(null);
    setError(null);
  };

  const handleCreateContact = async () => {
    console.log('🆕 Starting contact creation...');
    console.log('📝 Form data:', formData);
    
    if (!formData.surname) {
      setError(t('contactCreator.surnameRequired'));
      return;
    }

    setCreating(true);
    setError(null);

    try {
      // Prepare contact data WITHOUT email and phone (those go to contact_details)
      const { email, phone, ...contactData } = formData;
      
      const contactPayload = {
        ...contactData,
        country: 'Deutschland'
      };
      
      console.log('📤 Creating contact with payload:', contactPayload);
      
      // Step 1: Create the contact
      const result = await contactService.createContact(contactPayload);
      console.log('✅ Contact created:', result);

      if (!result.success) {
        throw new Error(result.message || 'Failed to create contact');
      }

      const contactId = result.id || result.data?.id;
      console.log('🆔 Contact ID:', contactId);

      if (!contactId) {
        throw new Error('No contact ID returned from API');
      }

      // Step 2: Create contact_details for email (if provided)
      if (email && email.trim()) {
        console.log('📧 Creating email contact detail...');
        const emailDetail = {
          contact_id: contactId,
          type: 'email',
          value: email.trim(),
          label: 'primary'
        };
        console.log('📤 Email detail payload:', emailDetail);
        
        const emailResult = await contactDetailsService.createContactDetail(emailDetail);
        console.log('✅ Email contact detail created:', emailResult);
      }

      // Step 2.5: Create contact_details for phone (if provided)
      if (phone && phone.trim()) {
        console.log('📞 Creating phone contact detail...');
        const phoneDetail = {
          contact_id: contactId,
          type: 'phone',
          value: phone.trim(),
          label: 'primary'
        };
        console.log('📤 Phone detail payload:', phoneDetail);
        
        const phoneResult = await contactDetailsService.createContactDetail(phoneDetail);
        console.log('✅ Phone contact detail created:', phoneResult);
      }

      // Step 3: Set the contact ID in the service
      console.log('🔗 Setting contact ID in service...');
      contactService.setContactId(contactId);
      
      // Step 4: Fetch the complete contact data
      console.log('📥 Fetching complete contact data...');
      const contactDataResponse = await contactService.getContact();
      console.log('✅ Contact data fetched:', contactDataResponse);
      
      onContactCreated(contactDataResponse.data);
    } catch (err) {
      console.error('❌ Error creating contact:', err);
      setError(err.message || t('contactCreator.createError'));
    } finally {
      setCreating(false);
    }
  };

  // Initial view - show email and ask what to do
  if (step === 'initial') {
    return (
      <div className="space-y-3">
        <div className={`bg-blue-50 border border-blue-200 rounded-lg ${currentTextSize.padding}`}>
          <div className="flex items-start gap-3">
            <MailRegular className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className={`${currentTextSize.bodyText} text-blue-900`}>
                {senderEmail 
                  ? t('contactCreator.emailNotAssignedMessage', { email: senderEmail })
                  : t('contactCreator.emailNotAssignedGeneric')
                }
              </p>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => setStep('selectType')}
            className={`flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white ${currentTextSize.padding} rounded-md transition-colors ${currentTextSize.bodyText} font-medium`}
          >
            <PersonAddRegular className="h-4 w-4" />
            {t('contactCreator.createNew')}
          </button>
          <button
            onClick={() => setStep('search')}
            className={`flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-blue-600 border border-blue-600 ${currentTextSize.padding} rounded-md transition-colors ${currentTextSize.bodyText} font-medium`}
          >
            <SearchRegular className="h-4 w-4" />
            {t('contactCreator.addToExisting')}
          </button>
        </div>
      </div>
    );
  }

  // Search existing contacts
  if (step === 'search') {
    return (
      <div className="space-y-3">
        {/* Show email being linked */}
        {senderEmail && (
          <div className={`bg-gray-50 border border-gray-200 rounded-lg ${currentTextSize.padding}`}>
            <div className="flex items-center gap-2">
              <MailRegular className="h-4 w-4 text-gray-600 flex-shrink-0" />
              <div className="flex-1">
                <p className={`${currentTextSize.bodyText} text-gray-700`}>
                  <span className="font-medium">{t('contactCreator.emailToLink')}:</span> {senderEmail}
                </p>
              </div>
            </div>
          </div>
        )}
        
        <div className={`bg-white border border-gray-200 rounded-lg ${currentTextSize.padding}`}>
          <div className="flex items-center gap-2 mb-3">
            <button
              onClick={() => {
                setStep('initial');
                setSearchQuery('');
                setSearchResults([]);
                setSelectedContact(null);
              }}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <ArrowLeftRegular className="h-4 w-4 text-gray-600" />
            </button>
            <h4 className={`${currentTextSize.headerText} font-semibold text-gray-900 flex-1`}>
              {t('contactCreator.searchExistingTitle')}
            </h4>
          </div>
          
          <div className="space-y-3">
            {/* Search input */}
            <div>
              <label className={`block ${currentTextSize.labelText} text-gray-700 mb-1`}>
                {t('contactCreator.searchExisting')}
              </label>
              <div className="relative">
                <SearchRegular className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('contactCreator.searchPlaceholder')}
                  className={`w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md ${currentTextSize.bodyText}`}
                />
              </div>
              
              {searching && (
                <div className={`mt-2 ${currentTextSize.bodyText} text-gray-500 flex items-center gap-2`}>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  {t('contactCreator.searching')}
                </div>
              )}
              
              {error && (
                <div className={`mt-2 p-2 bg-red-50 border border-red-200 rounded ${currentTextSize.bodyText} text-red-700`}>
                  {error}
                </div>
              )}
              
              {/* Search results dropdown */}
              {!searching && searchResults.length > 0 && !selectedContact && (
                <div className="mt-2 max-h-64 overflow-y-auto border border-gray-200 rounded-md bg-white">
                  {searchResults.map((contact) => (
                    <button
                      key={contact.id}
                      onClick={() => handleSelectContact(contact)}
                      className={`w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${currentTextSize.bodyText}`}
                    >
                      <div className="font-medium text-gray-900">
                        {contact.salutation && <span className="text-gray-600 mr-1">{contact.salutation}</span>}
                        {contact.name} {contact.surname}
                      </div>
                      {contact.email && (
                        <div className="text-gray-500 text-xs">{contact.email}</div>
                      )}
                      {(contact.city || contact.street_name) && (
                        <div className="text-gray-500 text-xs">
                          {contact.street_name} {contact.house_number}, {contact.zip_code} {contact.city}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
              
              {!searching && searchQuery.length >= 2 && searchResults.length === 0 && (
                <div className={`mt-2 ${currentTextSize.bodyText} text-gray-500 text-center py-2`}>
                  {t('contactCreator.noResultsFound')}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Confirmation dialog */}
        {selectedContact && (
          <div className={`bg-blue-50 border border-blue-200 rounded-lg ${currentTextSize.padding}`}>
            <div className="flex items-start gap-3 mb-3">
              <MailRegular className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className={`${currentTextSize.bodyText} text-blue-900`}>
                  {t('contactCreator.confirmLinkMessage', {
                    email: senderEmail,
                    name: `${selectedContact.name} ${selectedContact.surname}`
                  })}
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={handleCancelLinkContact}
                disabled={linkingContact}
                className={`flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 ${currentTextSize.padding} rounded-md transition-colors ${currentTextSize.bodyText} font-medium`}
              >
                {t('contactCreator.cancel')}
              </button>
              <button
                onClick={handleConfirmLinkContact}
                disabled={linkingContact}
                className={`flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white ${currentTextSize.padding} rounded-md transition-colors ${currentTextSize.bodyText} font-medium`}
              >
                {linkingContact ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    {t('contactCreator.linking')}
                  </>
                ) : (
                  <>
                    <CheckmarkRegular className="h-4 w-4" />
                    {t('contactCreator.confirm')}
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Select contact type (Person or Supplier)
  if (step === 'selectType') {
    return (
      <div className={`bg-white border border-gray-200 rounded-lg ${currentTextSize.padding}`}>
        <div className="flex items-center gap-2 mb-3">
          <button
            onClick={() => setStep('initial')}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
          >
            <ArrowLeftRegular className="h-4 w-4 text-gray-600" />
          </button>
          <h4 className={`${currentTextSize.headerText} font-semibold text-gray-900 flex-1`}>
            {t('contactCreator.selectTypeTitle')}
          </h4>
        </div>
        
        <div className="space-y-3">
          <p className={`${currentTextSize.bodyText} text-gray-600 mb-3`}>
            {t('contactCreator.selectTypeDescription')}
          </p>
          
          {/* Create new contact buttons */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => {
                setFormData(prev => ({ ...prev, type: 'person', sub_type: 'natürliche Person' }));
                setStep('createPerson');
              }}
              className={`flex flex-col items-center justify-center gap-2 p-4 border-2 border-gray-300 rounded-lg hover:border-blue-600 hover:bg-blue-50 transition-colors ${currentTextSize.bodyText}`}
            >
              <PersonRegular className="h-6 w-6 text-gray-600" />
              <span className="font-medium">{t('contactCreator.createPerson')}</span>
            </button>
            
            <button
              onClick={() => {
                setFormData(prev => ({ ...prev, type: 'supplier', sub_type: null }));
                setStep('createSupplier');
              }}
              className={`flex flex-col items-center justify-center gap-2 p-4 border-2 border-gray-300 rounded-lg hover:border-blue-600 hover:bg-blue-50 transition-colors ${currentTextSize.bodyText}`}
            >
              <BriefcaseRegular className="h-6 w-6 text-gray-600" />
              <span className="font-medium">{t('contactCreator.createSupplier')}</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Create person form
  if (step === 'createPerson' || step === 'createSupplier') {
    const isPerson = step === 'createPerson';
    
    return (
      <div className={`bg-white border border-gray-200 rounded-lg ${currentTextSize.padding}`}>
        <div className="flex items-center gap-2 mb-3">
          <button
            onClick={() => setStep('selectType')}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
          >
            <ArrowLeftRegular className="h-4 w-4 text-gray-600" />
          </button>
          <h4 className={`${currentTextSize.headerText} font-semibold text-gray-900 flex-1`}>
            {isPerson ? t('contactCreator.createPersonTitle') : t('contactCreator.createSupplierTitle')}
          </h4>
        </div>
        
        {error && (
          <div className={`mb-3 p-2 bg-red-50 border border-red-200 rounded ${currentTextSize.bodyText} text-red-700`}>
            {error}
          </div>
        )}

        <div className="space-y-3">
          {isPerson && (
            <div>
              <label className={`block ${currentTextSize.labelText} text-gray-700 mb-1`}>
                {t('contactCreator.salutation')}
              </label>
              <input
                type="text"
                value={formData.salutation}
                onChange={handleInputChange('salutation')}
                placeholder={t('contactCreator.salutationPlaceholder')}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md ${currentTextSize.bodyText}`}
              />
            </div>
          )}
          
          {isPerson && (
            <div>
              <label className={`block ${currentTextSize.labelText} text-gray-700 mb-1`}>
                {t('contactCreator.firstName')}
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={handleInputChange('name')}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md ${currentTextSize.bodyText}`}
              />
            </div>
          )}
          
          <div>
            <label className={`block ${currentTextSize.labelText} text-gray-700 mb-1`}>
              {isPerson ? t('contactCreator.lastName') : t('contactCreator.companyName')} *
            </label>
            <input
              type="text"
              value={formData.surname}
              onChange={handleInputChange('surname')}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md ${currentTextSize.bodyText}`}
              required
            />
          </div>

          {!isPerson && (
            <div>
              <label className={`block ${currentTextSize.labelText} text-gray-700 mb-1`}>
                {t('contactCreator.supplierType')}
              </label>
              {loadingSubTypes ? (
                <div className={`w-full px-3 py-2 border border-gray-300 rounded-md ${currentTextSize.bodyText} text-gray-500 flex items-center gap-2`}>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  {t('contactCreator.loadingSupplierTypes')}
                </div>
              ) : (
                <select
                  value={formData.sub_type || ''}
                  onChange={handleInputChange('sub_type')}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md ${currentTextSize.bodyText}`}
                >
                  {supplierSubTypes.length > 0 ? (
                    supplierSubTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))
                  ) : (
                    <option value={t('contactCreator.otherSupplierType')}>{t('contactCreator.otherSupplierType')}</option>
                  )}
                </select>
              )}
            </div>
          )}

          <div>
            <label className={`block ${currentTextSize.labelText} text-gray-700 mb-1 flex items-center gap-1`}>
              <MailRegular className="h-3 w-3" />
              {t('contactCreator.email')}
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={handleInputChange('email')}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md ${currentTextSize.bodyText}`}
            />
          </div>

          <div>
            <label className={`block ${currentTextSize.labelText} text-gray-700 mb-1 flex items-center gap-1`}>
              <PhoneRegular className="h-3 w-3" />
              {t('contactCreator.phone')}
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={handleInputChange('phone')}
              placeholder="+49..."
              className={`w-full px-3 py-2 border border-gray-300 rounded-md ${currentTextSize.bodyText}`}
            />
          </div>

          <div>
            <label className={`block ${currentTextSize.labelText} text-gray-700 mb-1`}>
              {t('contactCreator.notes')}
            </label>
            <textarea
              value={formData.notes}
              onChange={handleInputChange('notes')}
              rows={2}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md ${currentTextSize.bodyText}`}
            />
          </div>

          <div className="pt-2">
            <button
              onClick={handleCreateContact}
              disabled={creating || !formData.surname}
              className={`w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-md ${currentTextSize.bodyText} font-medium transition-colors`}
            >
              {creating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  {t('contactCreator.creating')}
                </>
              ) : (
                <>
                  <CheckmarkRegular className="h-4 w-4" />
                  {t('contactCreator.create')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

